import classnames from 'classnames';
import React, { useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';

import { analyticsPage } from 'analytics/utils';
import Footer from 'components/Footer';
import GlobalError from 'components/GlobalError';
import NavHeader from 'components/NavHeader';
import { removeGlobalError, stopLoader } from 'containers/App/actions';
import AppRouter from 'routes';
import { MY_QUOTES_NEXT_STEPS_PATH, MY_QUOTES_FULL_PATH } from 'routes/constants';
import useSelector from 'store/useSelector';
import { isValidCustomer } from 'util/isValidCustomer';

import { useNavigatorQuery } from './graphql/queries/Navigator/query';
import styles from './navigator.module.scss';
import { hasEditRestrictedAUAppliedPolicy } from './utils';

const Navigator: React.FC = () => {
  const dispatch = useDispatch();
  const error = useSelector((app) => app.error);
  const activeUserUuid = useSelector((app) => app.activeUserUuid);
  const lifeApplicationId = useSelector((app) => app.activeLifeApplicationId);
  const { pathname } = useLocation();

  const hideFooter = pathname === `${MY_QUOTES_FULL_PATH}/${MY_QUOTES_NEXT_STEPS_PATH}`;

  const { data, loading } = useNavigatorQuery({
    variables: {
      uuid: activeUserUuid,
      lifeApplicationId,
    },
  });

  const hasEmail = !!data?.customer?.identityProfile?.email;

  const validCustomer = isValidCustomer(JSON.parse(data?.customer?.userAttributes ?? '{}'));

  useEffect(() => {
    if (error) {
      dispatch(removeGlobalError());
    }

    dispatch(stopLoader());

    if (window?.optimizely?.push) {
      window.optimizely.push({
        type: 'activate',
      });
    }

    analyticsPage();
  }, [pathname]);

  const redirectToEstimates = useMemo(
    () => hasEditRestrictedAUAppliedPolicy(data?.lifeApplication.policies.appliedPolicy),
    [data]
  );

  const navigatorContentClasses = classnames('mod-navigator', styles.content, styles['help-tray']);

  return (
    <div className={classnames(styles.wrapper)} data-testid="navigator">
      <GlobalError error={error} />
      <div className={navigatorContentClasses}>
        <nav className={styles['nav-bar']}>
          <NavHeader
            email={data?.customer?.identityProfile.email}
            firstName={data?.customer?.identityProfile.firstName}
          />
        </nav>
        <div className={classnames(styles.main, styles['main-reveal-help-tray'])}>
          <AppRouter
            redirectToEstimates={redirectToEstimates}
            hasEmail={hasEmail}
            isLoading={loading}
            isValidCustomer={validCustomer}
          />
        </div>
      </div>
      {!hideFooter && <Footer />}
    </div>
  );
};

export default Navigator;
